import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { CalendarDialog } from "@/components/CalendarDialog";
import { Link } from "react-router-dom";
import {
  Globe,
  Smartphone,
  Zap,
  Shield,
  RefreshCw,
  BarChart3,
  CheckCircle,
  Phone,
  FileText,
  Bot,
  CalendarCheck,
  Search,
  Star,
  ArrowRight,
  MessageSquare,
  Mail,
  PhoneOff,
  LayoutDashboard,
  TabletSmartphone,
  Clock,
  Lock,
  Activity,
  Headphones,
  PenLine,
  Users,
  MapPin,
  Megaphone,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const featureGroups = [
  {
    title: "Lead Generation & Conversion",
    icon: Phone,
    items: [
      { icon: FileText, text: "Lead form with instant SMS & email alerts" },
      { icon: Mail, text: "Automated email & SMS follow-up sequences" },
      { icon: Phone, text: "Click-to-call button on every page" },
      { icon: PhoneOff, text: "Missed call text-back — never lose a lead" },
      { icon: MessageSquare, text: "All-in-one chat widget (live chat, SMS, social)" },
    ],
  },
  {
    title: "Marketing & SEO",
    icon: Search,
    items: [
      { icon: Search, text: "Advanced on-page SEO & local targeting" },
      { icon: Star, text: "Google review button & widget" },
      { icon: LayoutDashboard, text: "Analytics dashboard with lead tracking" },
      { icon: PenLine, text: "One content update per month included" },
    ],
  },
  {
    title: "Hosting, Security & Support",
    icon: Shield,
    items: [
      { icon: Lock, text: "Secure hosting with SSL & daily backups" },
      { icon: Activity, text: "Security & uptime monitoring" },
      { icon: Users, text: "CRM integration for lead tracking" },
      { icon: TabletSmartphone, text: "Mobile app access — manage leads on the go" },
      { icon: Headphones, text: "Priority support — 48-hour response time" },
    ],
  },
];

const optionalAddons = [
  "AI Chatbot for 24/7 instant enquiry responses",
  "Online booking system for appointment scheduling",
  "Custom integrations and advanced automation",
];

const benefits = [
  "More phone calls and quote requests from your website",
  "Professional online presence that builds trust instantly",
  "Mobile-friendly design that works on every device",
  "Fast updates whenever you need changes",
  "Show off reviews, certifications, and project galleries",
  "Turn website visitors into booked jobs automatically",
  "Higher Google rankings with SEO-optimised structure",
  "No upfront website costs — subscription-based pricing",
];

const faqs = [
  {
    question: "How much does a SMART Website cost?",
    answer:
      "Our SMART Websites are subscription-based, so there's no huge upfront cost. You get a professionally designed, mobile-first website with hosting, SSL, updates, and ongoing optimisation included in one affordable monthly fee. Book a strategy call for exact pricing.",
  },
  {
    question: "How long does it take to build my website?",
    answer:
      "Most SMART Websites are designed, built, and live within 2–3 weeks. We handle everything — content, design, development, and launch — so you can focus on running your business.",
  },
  {
    question: "Do I own my website?",
    answer:
      "While on the subscription, we manage everything for you — hosting, updates, security, and optimisation. If you ever cancel, we can transfer the site to you. Our goal is to earn your business every month with results.",
  },
  {
    question: "Can I update my website content?",
    answer:
      "Absolutely. Content updates are included in your subscription. Need to add new services, update photos, or change your phone number? Just let us know and we'll handle it — usually within 24–48 hours.",
  },
  {
    question: "Will my website help me rank on Google?",
    answer:
      "Yes. Every SMART Website is built with SEO best practices — proper heading structure, schema markup, fast load speeds, mobile-first design, and local targeting. It's the foundation for ranking in your area and getting found by customers searching for your services.",
  },
];

const websiteExamples = [
  { src: "/images/website-example-1.png", label: "Electrical Business" },
  { src: "/images/website-example-2.png", label: "Roofing Business" },
  { src: "/images/website-example-3.png", label: "HVAC Business" },
  { src: "/images/website-example-4.png", label: "Building Business" },
  { src: "/images/website-example-5.png", label: "Appliance Repair Business" },
  { src: "/images/website-example-6.png", label: "Construction Business" },
  { src: "/images/website-example-7.png", label: "Landscaping Business" },
  { src: "/images/website-example-8.png", label: "Plumbing Business" },
];

export default function Websites() {
  return (
    <>
      <Helmet>
        <title>SMART Websites for Tradies | Mobile-First Trade Websites | FullScale Marketing</title>
        <meta
          name="description"
          content="Professional, mobile-first SMART websites for Australian tradies and construction businesses. Subscription-based websites that generate leads, phone calls, and booked jobs."
        />
        <link rel="canonical" href="https://fullscalemarketing.com.au/services/websites" />
        <meta property="og:title" content="SMART Websites for Tradies | FullScale Marketing" />
        <meta
          property="og:description"
          content="Fast, lead-generating, mobile-first websites built for Australian trade businesses. No upfront costs — subscription model."
        />
        <meta property="og:url" content="https://fullscalemarketing.com.au/services/websites" />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-28 sm:pt-32 lg:pt-40 pb-16 sm:pb-20 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-[hsl(100_25%_97%)] to-[hsl(98_30%_95%/0.6)]" />
        <div className="absolute inset-0 bg-gradient-hero-glow pointer-events-none" />
        <div className="absolute inset-0 grid-pattern opacity-30" />

        <div className="container-wide relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="section-badge mb-4">
                <span className="text-xs">SMART Websites</span>
              </div>
              <h1 className="text-[1.75rem] sm:text-[2.5rem] lg:text-[3.25rem] font-extrabold text-foreground leading-[1.1] mb-5 tracking-[-0.025em]">
                SMART Websites for{" "}
                <span className="text-gradient">Trade Businesses</span>
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-muted-foreground leading-[1.7] max-w-2xl mx-auto mb-8">
                Fast, professional, mobile-first websites designed to turn visitors into leads, phone calls, and jobs.
              </p>
              <CalendarDialog
                buttonVariant="default"
                buttonSize="lg"
                buttonClassName="h-14 text-sm font-bold rounded-xl"
              />
            </motion.div>

          </div>
        </div>
      </section>

      {/* Intro / Why It Matters */}
      <section className="section-padding bg-muted relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="container-wide relative z-10">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex items-start gap-4"
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                <Globe className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-foreground mb-3">
                  Your Website Is Your Digital Shopfront
                </h2>
                <p className="text-foreground text-base sm:text-lg leading-[1.75]">
                  If it looks outdated, loads slowly, or doesn't work on mobile — you're losing jobs to competitors. Our SMART websites are built specifically for trade businesses to{" "}
                  <strong className="text-primary">generate more enquiries and booked jobs</strong> while building trust online.
                </p>
              </div>
            </motion.div>

            {/* Key stats */}
            <div className="grid grid-cols-2 gap-4 mt-8">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1, duration: 0.4 }}
                className="bg-card rounded-xl p-5 border border-border/50 text-center"
              >
                <p className="text-2xl sm:text-3xl font-extrabold text-primary mb-1">80%</p>
                <p className="text-muted-foreground text-sm">of customers search on mobile</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.4 }}
                className="bg-card rounded-xl p-5 border border-border/50 text-center"
              >
                <p className="text-2xl sm:text-3xl font-extrabold text-primary mb-1">&lt;2s</p>
                <p className="text-muted-foreground text-sm">page load speed guaranteed</p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included / Features */}
      <section className="section-padding bg-background relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-25" />
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8 sm:mb-10"
          >
            <h2 className="heading-section text-foreground mb-3">
              What's <span className="text-gradient">Included</span>
            </h2>
            <p className="text-muted-foreground text-base max-w-xl mx-auto">
              Every SMART Website is purpose-built for trade businesses — no templates, no fluff.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {featureGroups.map((group, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="bg-card rounded-xl border border-border/60 shadow-card overflow-hidden"
              >
                <div className="flex items-center gap-3 p-5 pb-4 border-b border-border/40">
                  <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <group.icon className="w-4.5 h-4.5 text-primary" />
                  </div>
                  <h3 className="text-[0.9375rem] font-bold text-foreground">{group.title}</h3>
                </div>
                <ul className="p-5 space-y-3">
                  {group.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2.5">
                      <item.icon className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-muted-foreground text-[0.875rem] leading-[1.5]">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Optional Add-ons */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="mt-8 max-w-2xl mx-auto bg-muted rounded-xl p-5 border border-border/50"
          >
            <p className="text-foreground font-bold text-sm mb-3">Optional Add-ons:</p>
            <div className="space-y-2">
              {optionalAddons.map((addon, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-[0.9375rem]">{addon}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="section-padding bg-background relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-25" />
        <div className="container-wide relative z-10">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-8"
            >
              <h2 className="heading-section text-foreground mb-3">
                Key <span className="text-gradient">Benefits</span>
              </h2>
            </motion.div>

            <div className="grid sm:grid-cols-2 gap-3">
              {benefits.map((benefit, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.4 }}
                  className="flex items-start gap-3 bg-card rounded-lg p-4 border border-border/50"
                >
                  <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-foreground text-[0.9375rem] leading-[1.5]">
                    {benefit}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Website Examples Gallery */}
      <section className="section-padding bg-muted relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8 sm:mb-10"
          >
            <h2 className="heading-section text-foreground mb-3">
              Websites We've <span className="text-gradient">Built</span>
            </h2>
            <p className="text-muted-foreground text-base max-w-xl mx-auto">
              Examples of SMART Websites we design for Australian trade businesses.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
            {websiteExamples.map((example, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.4 }}
                className="group bg-card rounded-xl border border-border/60 shadow-card overflow-hidden select-none"
              >
                <div className="aspect-[3/4] overflow-hidden">
                  <img
                    src={example.src}
                    alt={`${example.label} website template`}
                    className="w-full h-full object-cover object-top pointer-events-none"
                    draggable={false}
                  />
                </div>
                <div className="px-3 py-2.5 border-t border-border/40">
                  <p className="text-foreground text-xs font-semibold text-center">{example.label}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-background relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-25" />
        <div className="container-wide relative z-10">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-8"
            >
              <h2 className="heading-section text-foreground mb-3">
                Frequently Asked <span className="text-gradient">Questions</span>
              </h2>
            </motion.div>

            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05, duration: 0.4 }}
                >
                  <AccordionItem
                    value={`faq-${i}`}
                    className="bg-card rounded-xl border border-border/60 px-5 shadow-card"
                  >
                    <AccordionTrigger className="text-foreground text-[0.9375rem] font-semibold text-left hover:no-underline py-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-[0.9375rem] leading-[1.7] pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="section-padding bg-muted relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-40" />
        <div className="container-wide relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-8"
          >
            <h2 className="heading-section text-foreground mb-3">
              Related <span className="text-gradient">Services</span>
            </h2>
            <p className="text-muted-foreground text-base max-w-xl mx-auto leading-[1.7]">
              Get even better results by combining your SMART Website with these services.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {[
              {
                icon: MapPin,
                title: "Google Business Profile & SEO",
                description: "Rank higher in local search and Google Maps to drive organic traffic to your new website.",
                href: "/services/gbp-seo",
              },
              {
                icon: Megaphone,
                title: "Lead Generation Ads",
                description: "Drive targeted traffic to your SMART Website with Google & Facebook ad campaigns.",
                href: "/services/lead-generation",
              },
              {
                icon: Star,
                title: "Reputation & Reviews",
                description: "Showcase 5-star reviews on your website to build trust and increase conversions.",
                href: "/services/reputation",
              },
            ].map((service, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
              >
                <Link
                  to={service.href}
                  className="group block bg-card rounded-xl p-5 border border-border/60 shadow-card hover:shadow-xl hover:border-primary/30 transition-all duration-300 h-full"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/20 transition-colors">
                    <service.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-base font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-[1.6] mb-3">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-primary text-sm font-semibold group-hover:gap-2.5 transition-all">
                    Learn more
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Back link */}
      <div className="py-6 text-center bg-background">
        <Link
          to="/services"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary text-sm font-medium transition-colors"
        >
          ← Back to all services
        </Link>
      </div>
    </>
  );
}
